import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { del, get, patch, post } from "../lib/api";
import { channelIcon, channelLabel, formatDay } from "../lib/format";
import { todayISO } from "../../helpers/datetime";
import { Button, Empty, ErrorBox, Loading } from "../ui";
import { useShell } from "../shell/context";
import { notePath } from "../lib/notes";

const PAGE_SIZE = 50;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthOf = (ymd) => (ymd ? `${MONTHS[+ymd.slice(5, 7) - 1]} ${ymd.slice(0, 4)}` : "");

// Touches of a contact or company, newest first, grouped by month.
// context: "contact" hides the contact name, "company" shows it.
// noStartLead hides the "Start lead" action (on a lead's own timeline).
// mentionsPath (E11) adds daily-note mentions as their own item type;
// entity {id, name} is the record (for "Log as touch").
export default function Timeline({ path, mentionsPath, entity, context, version, onChanged, noStartLead }) {
    const { openEditTouch, openLead, openLog, toast, bump } = useShell();
    const [mentions, setMentions] = useState([]);
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const today = todayISO();

    const load = useCallback(
        async (p) => {
            setLoading(true);
            setError(null);
            try {
                const res = await get(path, { page: p, page_size: PAGE_SIZE });
                setItems((prev) => (p === 1 ? res.data || [] : [...prev, ...(res.data || [])]));
                setCount(res.count || 0);
                setPage(p);
            } catch (e) {
                setError(e);
            } finally {
                setLoading(false);
            }
        },
        [path]
    );

    useEffect(() => {
        load(1);
    }, [load, version]);

    const loadMentions = useCallback(() => {
        if (!mentionsPath) return;
        get(mentionsPath, { page_size: 100 })
            .then((res) => setMentions(res.data || []))
            .catch(() => setMentions([]));
    }, [mentionsPath]);

    useEffect(() => {
        loadMentions();
    }, [loadMentions, version]);

    const logMention = (m) =>
        openLog({
            [context === "company" ? "company" : "contact"]: entity ? { id: entity.id, name: entity.name } : null,
            touch_date: m.note_date,
            summary: m.text,
            mentionId: m.id,
            onLogged: loadMentions,
        });

    const changed = () => {
        bump();
        if (onChanged) onChanged();
    };

    const remove = async (t) => {
        try {
            await del(`v1/crm/touches/${t.id}`);
            toast("Touch moved to the trash.", {
                icon: "ri-delete-bin-line",
                action: { label: "Undo", run: () => post(`v1/crm/trash/touches/${t.id}/restore`).then(changed).catch((e) => toast(e.message, { error: true })) },
            });
            changed();
        } catch (e) {
            toast(e.message, { error: true });
        }
    };

    const setDone = async (t, done) => {
        try {
            if (done) await post(`v1/crm/touches/${t.id}/follow-up-done`);
            else await patch(`v1/crm/touches/${t.id}`, { follow_up_done: false });
            toast(done ? "Follow-up done." : "Follow-up reopened.");
            changed();
        } catch (e) {
            toast(e.message, { error: true });
        }
    };

    if (loading && items.length === 0) return <Loading />;
    // Merge mentions by date; while older touches are not loaded yet, only
    // mentions within the loaded range are shown.
    const oldest = items.length < count && items.length ? items[items.length - 1].touch_date : null;
    const merged = [...items.map((t) => ({ kind: "touch", date: t.touch_date, t })),
        ...mentions.filter((m) => !oldest || m.note_date >= oldest).map((m) => ({ kind: "mention", date: m.note_date, m }))]
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.kind === b.kind ? 0 : a.kind === "touch" ? -1 : 1));
    let lastMonth = null;
    return (
        <div style={{ padding: "4px 16px 16px" }}>
            <ErrorBox error={error} onRetry={() => load(1)} />
            {merged.length === 0 && !error && <Empty>No touches yet.</Empty>}
            {merged.map((row) => {
                const month = monthOf(row.date);
                if (row.kind === "mention") {
                    const m = row.m;
                    const showM = month !== lastMonth;
                    lastMonth = month;
                    return (
                        <React.Fragment key={"m" + m.id}>
                            {showM && <div className="hz-tl-month">{month}</div>}
                            <div className="hz-tl-item">
                                <span className="hz-tl-icon mention"><i className="ri-sticky-note-line"></i></span>
                                <div className="hz-tl-body">
                                    <div className="hz-tl-title">
                                        <span className="hz-strong">
                                            Mentioned in daily note
                                            <span className="hz-xs hz-muted" style={{ fontWeight: 400 }}> · not a touch</span>
                                        </span>
                                        <span className="hz-small hz-muted hz-nowrap">{formatDay(m.note_date, { short: true })}</span>
                                    </div>
                                    <div className="hz-pre" style={{ color: "var(--hz-gray-700)", marginTop: 2 }}>
                                        {m.is_task && <i className={m.done ? "ri-checkbox-line" : "ri-checkbox-blank-line"} style={{ marginRight: 4 }}></i>}
                                        {m.text}
                                    </div>
                                    <div className="hz-tl-meta">
                                        <Link to={notePath(m.note_date)} className="hz-btn-link"><i className="ri-sticky-note-line"></i> Open note</Link>
                                        {m.touch_id ? (
                                            <span className="hz-small hz-muted"><i className="ri-check-line"></i> Logged as touch</span>
                                        ) : (
                                            <button type="button" className="hz-btn-link" onClick={() => logMention(m)}><i className="ri-chat-new-line"></i> Log as touch</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                }
                const t = row.t;
                const showMonth = month !== lastMonth;
                lastMonth = month;
                const hasFollowUp = !!(t.follow_up || t.follow_up_date);
                const fuOverdue = hasFollowUp && !t.follow_up_done_at && t.follow_up_date && t.follow_up_date < today;
                const who = [];
                if (context !== "contact" && t.contact_id) who.push(<Link key="c" to={`/crm/contacts/${t.contact_id}`}>{t.contact_name || "Contact"}</Link>);
                if (context !== "company" && t.company_id) who.push(<Link key="co" to={`/crm/companies/${t.company_id}`}>{t.company_name || "Company"}</Link>);
                return (
                    <React.Fragment key={t.id}>
                        {showMonth && <div className="hz-tl-month">{month}</div>}
                        <div className="hz-tl-item">
                            <span className="hz-tl-icon"><i className={channelIcon(t.channel)}></i></span>
                            <div className="hz-tl-body">
                                <div className="hz-tl-title">
                                    <span className="hz-strong">
                                        {channelLabel(t.channel)}
                                        {who.length > 0 && <span style={{ fontWeight: 400 }}> · {who.reduce((acc, el, i) => (i ? [...acc, " at ", el] : [el]), [])}</span>}
                                        {t.source === "import" && <span className="hz-xs hz-muted"> · imported</span>}
                                    </span>
                                    <span className="hz-small hz-muted hz-nowrap">{formatDay(t.touch_date, { short: true })}</span>
                                </div>
                                {t.summary && <div className="hz-pre" style={{ color: "var(--hz-gray-700)", marginTop: 2 }}>{t.summary}</div>}
                                {t.on_their_mind && (
                                    <div style={{ marginTop: 4 }}>
                                        <span className="hz-tl-label">On their mind</span>
                                        <span className="hz-pre">{t.on_their_mind}</span>
                                    </div>
                                )}
                                {hasFollowUp && (
                                    <div className={"hz-followup-box" + (fuOverdue ? " overdue" : "")}>
                                        <i className={t.follow_up_done_at ? "ri-checkbox-circle-line" : "ri-flag-line"} style={{ color: t.follow_up_done_at ? "var(--hz-success-text)" : fuOverdue ? "var(--hz-danger)" : "var(--hz-warning-text)" }}></i>
                                        <span className="hz-spacer" style={{ textDecoration: t.follow_up_done_at ? "line-through" : "none", color: t.follow_up_done_at ? "var(--hz-text-muted)" : undefined }}>
                                            {t.follow_up || "Follow-up"}
                                            {t.follow_up_date && <span className={"hz-small " + (fuOverdue ? "hz-status-danger" : "hz-muted")}> · {formatDay(t.follow_up_date, { short: true })}</span>}
                                        </span>
                                        {t.follow_up_done_at ? (
                                            <Button size="sm" variant="ghost" onClick={() => setDone(t, false)}>Reopen</Button>
                                        ) : (
                                            <Button size="sm" variant="soft-success" icon="ri-check-line" onClick={() => setDone(t, true)}>Done</Button>
                                        )}
                                    </div>
                                )}
                                <div className="hz-tl-meta">
                                    <button type="button" className="hz-btn-link" onClick={() => openEditTouch(t)}><i className="ri-pencil-line"></i> Edit</button>
                                    {!noStartLead && (
                                        <button type="button" className="hz-btn-link" onClick={() => openLead({ touch: t })}><i className="ri-funds-line"></i> Start lead</button>
                                    )}
                                    <button type="button" className="hz-btn-link danger" onClick={() => remove(t)}><i className="ri-delete-bin-line"></i> Delete</button>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                );
            })}
            {items.length < count && (
                <div style={{ paddingTop: 12 }}>
                    <Button size="sm" variant="outline" disabled={loading} onClick={() => load(page + 1)}>Show older</Button>
                </div>
            )}
        </div>
    );
}
